import { useState, useEffect, useCallback, useRef } from 'react';
import { useWallet } from '@solana/wallet-adapter-react';
import { api } from '../services/api.js';
import logger from '../utils/logger';

export function useWalletTokens() {
  const { publicKey, connected } = useWallet();
  const [tokens, setTokens] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const hasAttemptedRef = useRef(false);

  const fetchWalletTokens = useCallback(async (force = false) => {
    if (!connected || !publicKey) {
      setTokens([]);
      return;
    }

    if (!force && hasAttemptedRef.current) {
      return;
    }

    setLoading(true);
    setError(null);
    hasAttemptedRef.current = true;

    try {
      const address = publicKey.toBase58();
      const data = await api.getUserCoins(address, 0, 50, false);
      
      if (data.success) {
        setTokens(data.tokens || []);
      } else {
        throw new Error(data.error || 'Failed to fetch wallet tokens');
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        logger.warn('Token fetch unavailable - tokens will load from launch history');
      }
      setTokens([]);
    } finally {
      setLoading(false);
    }
  }, [publicKey, connected]);

  // Reset attempt flag when wallet changes
  useEffect(() => {
    hasAttemptedRef.current = false;
  }, [publicKey?.toBase58()]);

  // Fetch on mount and when wallet changes
  useEffect(() => {
    fetchWalletTokens();
  }, [fetchWalletTokens]);

  return {
    tokens,
    loading,
    error,
    refetch: () => fetchWalletTokens(true) // Force refetch when manually called
  };
}

export default useWalletTokens;
